// ─────────────────────────────────────────────────────────────────────────────
// Position « hors grille » d'un employé — fonctions pures, importables côté
// client (aucun Firestore). Un employé hors grille (Compensation.off_grid)
// garde une classe + un niveau (rôle, regroupement de masse salariale) mais son
// taux RÉEL sort de la plage de la grille : la direction (+/−), l'échelon de
// référence et l'écart sont DÉRIVÉS ici à la lecture — jamais stockés — pour
// rester vrais quand le taux ou la grille changent. Comparer à la grille de
// l'ANNÉE courante (resolveYearGrid) : la grille a changé en 2026 et certains
// employés sont en transition.
// ─────────────────────────────────────────────────────────────────────────────

import { round2 } from './projection'
import { maxEchelonForNiveau } from './niveau'
import type { SalaryClass } from '../types/firestore'

// 'above' = au-dessus du plafond du niveau (« Hors grille + »),
// 'below' = sous l'échelon 1 (« Hors grille − »),
// 'in_range' = drapeau posé mais taux dans la plage (à vérifier par la gestion).
export type OffGridDirection = 'above' | 'below' | 'in_range'

export type OffGridPosition = {
  direction: OffGridDirection
  // Échelon de la grille qui sert de référence : plafond du niveau (above,
  // in_range) ou échelon 1 (below). C'est « où il devrait être ».
  referenceEchelon: number
  referenceRate: number
  // Écart du taux réel vs la référence — signé ($ et fraction). Pour in_range,
  // l'écart vs plafond reste informatif (négatif ou nul).
  deltaAbs: number
  deltaPct: number | null
}

// Dérive la position hors grille depuis le taux réel et la classe/niveau.
// Retourne null si la dérivation est impossible : taux réel absent (la carte
// taux affiche alors « taux réel requis »), classe absente ou échelle vide.
export function offGridPosition(
  realRate: number | null,
  cls: Pick<SalaryClass, 'echelons' | 'niveaux'> | null,
  niveau: number | null,
): OffGridPosition | null {
  if (realRate == null || !cls || cls.echelons.length === 0) return null

  const capEchelon = maxEchelonForNiveau(cls, niveau)
  const cap = cls.echelons.find((e) => e.echelon === capEchelon)
  const floor = cls.echelons.find((e) => e.echelon === 1) ?? cls.echelons[0]
  if (!cap) return null

  const direction: OffGridDirection =
    realRate > cap.rate ? 'above' : realRate < floor.rate ? 'below' : 'in_range'
  const ref = direction === 'below' ? floor : cap
  const deltaAbs = round2(realRate - ref.rate)

  return {
    direction,
    referenceEchelon: ref.echelon,
    referenceRate: ref.rate,
    deltaAbs,
    deltaPct: ref.rate > 0 ? deltaAbs / ref.rate : null,
  }
}

// Libellé court du badge : « Hors grille + » / « Hors grille − » / « Hors grille ».
export function offGridBadgeLabel(pos: OffGridPosition | null): string {
  if (!pos) return 'Hors grille'
  if (pos.direction === 'above') return 'Hors grille +'
  if (pos.direction === 'below') return 'Hors grille −'
  return 'Hors grille'
}
